/**
 * ResolutionRequestContract (AJIP-16.02.006 · WS1)
 * ---------------------------------------------------------------------
 * Canonical, immutable shape for a Journey Resolution Request. Every
 * consumer of the Resolution Service must supply a request that
 * conforms to this contract. Untrusted input.
 *
 * Governance
 *   * No credentials, tokens, session objects, or PII may appear in
 *     any field. See FORBIDDEN_REQUEST_KEYS.
 *   * No executable code (Function-typed values) is accepted.
 *   * Payload is bounded to MAX_REQUEST_BYTES.
 */

// Fields the validator will inspect. Optional fields are marked in the
// validator via presence checks rather than a separate structure so
// this list stays the single source of truth for "known keys".
const KNOWN_REQUEST_FIELDS = Object.freeze([
  // ── Identification ──────────────────────────────────────────────
  'requestId',
  'correlationRef',
  'requestedAt',
  'canonReference',
  // ── Journey targeting ───────────────────────────────────────────
  'journeyId',
  'journeyType',
  'journeyVersion',
  // ── Context ─────────────────────────────────────────────────────
  'principalContext',
  'discoveryContext',
  // ── Entry ───────────────────────────────────────────────────────
  'entryPoint',
  'requiredFeatureFlags',
  // ── Existing state ──────────────────────────────────────────────
  'existingInstanceRef',
  'resumeIntent',
  'activationIntent',
  // ── Audit-safe metadata ─────────────────────────────────────────
  'metadata',
]);

const REQUIRED_REQUEST_FIELDS = Object.freeze([
  'requestId',
  'principalContext',
  'discoveryContext',
  'entryPoint',
  'requiredFeatureFlags',
  'activationIntent',
]);

// Superset of every prohibited key across the framework. Case
// insensitive comparison. Any occurrence at any depth is rejected.
const FORBIDDEN_REQUEST_KEYS = Object.freeze([
  'password', 'token', 'apiKey', 'secret', 'credential',
  'cardNumber', 'cvv', 'privateKey', 'sessionId',
  'advertisingId', 'behaviouralProfile',
  'providerSession', 'paymentInfo', 'accessToken', 'refreshToken',
  'bearer', 'authorization',
]);

// Canonical entry points. This vocabulary is deliberately narrow. New
// entry points require an explicit implementation package.
const ENTRY_POINTS = Object.freeze([
  'internal-boot',
  'internal-test',
  'internal-orchestration',
  'system-restore',
]);

// Cap the entire request payload to prevent hostile input from
// blocking the framework.
const MAX_REQUEST_BYTES = 8 * 1024;

export {
  KNOWN_REQUEST_FIELDS,
  REQUIRED_REQUEST_FIELDS,
  FORBIDDEN_REQUEST_KEYS,
  ENTRY_POINTS,
  MAX_REQUEST_BYTES,
};
